import React, { useState, useEffect } from "react";
import {
  Search,
  Download,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  Sheet,
  FileText,
} from "lucide-react";
import ExcelJS from "exceljs";
import { getBankAndBranchLogo } from "../../../../../Services/EndPoints";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { postDataWithBody } from "../../../../../Services/Services";

const TableComponent = ({
  data,
  columns,
  subColumns,
  showDownload,
  showPDFDownload,
  showSearch,
  showPagination,
  enableSorting,
  tableType,
  title,
  showDateFilter,
  item,
  tablePagination,
  tableRowsPerPage,
  onTablePageChange,
  onTableRowsPerPageChange,
  selectedBankIds,
  selectedBranchIds,
  savedQueries,
  selectedDateField,
  getThemeURL,
  getData,
  instanceBankIds,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [appliedDateRange, setAppliedDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [expandedRows, setExpandedRows] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const [tableData, setTableData] = useState(data || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [loadingQueries, setLoadingQueries] = useState(false);
  const [queryError, setQueryError] = useState(null);
  const [logoFromApi, setLogoFromApi] = useState(null);
  const [bankName, setBankName] = useState("");
  const [bankData, setBankData] = useState(null);

  console.log("Table: Component props:", {
    selectedBankIds,
    selectedBranchIds,
    hasDataSource: !!item?.dataSource,
    savedQueriesLength: savedQueries?.length || 0,
    dataLength: data?.length || 0,
  });

  // User profile and logo fetching functions
  const getUserProfile = async () => {
    try {
      const profile = await getData(getThemeURL);
      if (profile && profile.length > 0) {
        // setUserProfile(profile[0]);
        if (item.showBankLogo) {
          fetchLogoAndNameFromApi(profile[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      // setUserProfile(null);
    }
  };

  const createPayload = (profile) => {
    let admin_role = "U";

    if (profile.is_admin === true) {
      admin_role = "HO";
    } else if (profile.role_name === "SUPER ADMIN") {
      admin_role = "SA";
    }

    return {
      admin_role: admin_role,
      bank_id: profile.bank_id || null,
      branch_id: profile.branch_id || null,
    };
  };

  const byteArrayToImageUrl = (byteArray) => {
    try {
      const uint8Array = new Uint8Array(byteArray);
      const blob = new Blob([uint8Array], { type: "image/png" });
      const imageUrl = URL.createObjectURL(blob);
      return imageUrl;
    } catch (error) {
      console.error("Error converting byte array to image:", error);
      return null;
    }
  };

  const fetchLogoAndNameFromApi = async (profile) => {
    try {
      const payload = createPayload(profile);
      const response = await postDataWithBody(
        getBankAndBranchLogo,
        payload,
        {}
      );

      if (Array.isArray(response) && response.length > 0) {
        const bankData = response[0];
        setBankData(bankData);

        if (bankData.bankName) {
          setBankName(bankData.bankName);
        } else if (bankData.branchName) {
          setBankName(bankData.branchName);
        }

        if (
          bankData.hasOwnProperty("image") &&
          Array.isArray(bankData.image) &&
          bankData.image.length > 0
        ) {
          const imageUrl = byteArrayToImageUrl(bankData.image);
          setLogoFromApi(imageUrl);
        } else {
          setLogoFromApi(null);
        }
      }
    } catch (error) {
      console.error("Error fetching logo and bank name from API:", error);
      setLogoFromApi(null);
    }
  };

  useEffect(() => {
    if (item.showBankLogo) {
      getUserProfile();
    }
  }, [item.showBankLogo]);

  useEffect(() => {
    return () => {
      if (logoFromApi) {
        URL.revokeObjectURL(logoFromApi);
      }
    };
  }, [logoFromApi]);

  // Transform NiFi data to table format - SAME AS CARD
  const transformNiFiDataToTableFormat = (nifiResponse) => {
    console.log("Table: Raw API Response received:", nifiResponse);

    try {
      // PRIORITY 1: Handle aggregated data structure (NEW)
      if (nifiResponse && nifiResponse.aggregatedData) {
        console.log(
          "Table: Processing aggregatedData:",
          nifiResponse.aggregatedData
        );

        const { aggregatedData } = nifiResponse;

        if (
          aggregatedData.status === "success" &&
          aggregatedData.data &&
          Array.isArray(aggregatedData.data)
        ) {
          console.log("Table: Aggregated data validation passed");

          if (
            aggregatedData.data.length > 0 &&
            typeof aggregatedData.data[0] === "object"
          ) {
            console.log(
              "Table: Aggregated data is in object format, returning:",
              aggregatedData.data
            );
            return aggregatedData.data;
          }

          if (
            aggregatedData.columns &&
            Array.isArray(aggregatedData.columns) &&
            aggregatedData.data.length > 0 &&
            Array.isArray(aggregatedData.data[0])
          ) {
            console.log(
              "Table: Transforming aggregated data from array format"
            );

            const transformedData = aggregatedData.data.map((row) => {
              const dataPoint = {};

              aggregatedData.columns.forEach((column, index) => {
                let cleanKey = column
                  .replace(/^(SUM|COUNT|AVG|MIN|MAX|DISTINCT)\(/i, "")
                  .replace(/\)$/, "")
                  .replace(/^.*\./, "");

                let value = row[index];

                if (
                  cleanKey.toLowerCase().includes("date") &&
                  typeof value === "number" &&
                  value > 1000000000000
                ) {
                  try {
                    value = new Date(value).toISOString().split("T")[0];
                  } catch (error) {
                    console.warn(
                      "Table: Error converting timestamp:",
                      value,
                      error
                    );
                  }
                }

                dataPoint[cleanKey] = value;
              });

              return dataPoint;
            });

            console.log(
              "Table: Transformed aggregated data from array format:",
              transformedData
            );
            return transformedData;
          }
        } else if (aggregatedData.recordCount === 0) {
          console.log(
            "Table: Aggregated data has no records matching criteria"
          );
          return [];
        }
      }

      // PRIORITY 2: Handle multi-bank response structure
      else if (
        nifiResponse &&
        nifiResponse.responses &&
        Array.isArray(nifiResponse.responses)
      ) {
        console.log("Table: Processing multi-bank responses (fallback)");
        let allTransformedData = [];

        nifiResponse.responses.forEach((bankResponse, bankIndex) => {
          console.log(
            `Table: Processing bank ${bankResponse.bankId} response:`,
            bankResponse
          );

          if (bankResponse.status === "success" && bankResponse.data) {
            const bankData = bankResponse.data;

            if (
              bankData.status === "success" &&
              bankData.data &&
              Array.isArray(bankData.data)
            ) {
              if (
                bankData.data.length > 0 &&
                typeof bankData.data[0] === "object"
              ) {
                console.log(
                  `Table: Bank ${bankResponse.bankId} data is already in object format`
                );
                allTransformedData = allTransformedData.concat(bankData.data);
              } else if (
                bankData.columns &&
                Array.isArray(bankData.columns) &&
                bankData.data.length > 0 &&
                Array.isArray(bankData.data[0])
              ) {
                console.log(
                  `Table: Transforming Bank ${bankResponse.bankId} data from array format`
                );

                const transformedBankData = bankData.data.map((row) => {
                  const dataPoint = {};

                  bankData.columns.forEach((column, index) => {
                    let cleanKey = column
                      .replace(/^(SUM|COUNT|AVG|MIN|MAX|DISTINCT)\(/i, "")
                      .replace(/\)$/, "")
                      .replace(/^.*\./, "");

                    let value = row[index];

                    if (
                      cleanKey.toLowerCase().includes("date") &&
                      typeof value === "number" &&
                      value > 1000000000000
                    ) {
                      try {
                        value = new Date(value).toISOString().split("T")[0];
                      } catch (error) {
                        console.warn(
                          "Table: Error converting timestamp:",
                          value,
                          error
                        );
                      }
                    }

                    dataPoint[cleanKey] = value;
                  });

                  if (!dataPoint.bank_id && !dataPoint.bankId) {
                    dataPoint.bank_id = bankResponse.bankId;
                  }

                  if (!dataPoint.bank_name && !dataPoint.bankName) {
                    const bankNameIndex = bankData.columns.findIndex(
                      (col) =>
                        col.toLowerCase().includes("bank_name") ||
                        col.toLowerCase().includes("bankname")
                    );
                    if (bankNameIndex !== -1 && row[bankNameIndex]) {
                      dataPoint.bank_name = row[bankNameIndex];
                    }
                  }

                  return dataPoint;
                });

                allTransformedData =
                  allTransformedData.concat(transformedBankData);
              }
            } else if (bankData.recordCount === 0) {
              console.log(
                `Table: Bank ${bankResponse.bankId} has no records matching criteria`
              );
            }
          } else {
            console.warn(
              `Table: Bank ${bankResponse.bankId} response failed:`,
              bankResponse
            );
          }
        });

        console.log(
          "Table: Final transformed multi-bank data:",
          allTransformedData
        );
        return allTransformedData;
      }

      // PRIORITY 3: Handle single response format
      else if (
        nifiResponse &&
        nifiResponse.status === "success" &&
        nifiResponse.data
      ) {
        console.log("Table: Processing single response format (fallback)");
        const { data } = nifiResponse;

        if (
          Array.isArray(data) &&
          data.length > 0 &&
          typeof data[0] === "object"
        ) {
          console.log(
            "Table: Single response - data is already in object format"
          );
          return data;
        }

        if (
          nifiResponse.columns &&
          Array.isArray(data) &&
          Array.isArray(data[0])
        ) {
          const { columns } = nifiResponse;
          const transformedData = data.map((row) => {
            const dataPoint = {};
            columns.forEach((column, index) => {
              let cleanKey = column
                .replace(/^(SUM|COUNT|AVG|MIN|MAX|DISTINCT)\(/i, "")
                .replace(/\)$/, "")
                .replace(/^.*\./, "");

              let value = row[index];
              if (
                cleanKey.toLowerCase().includes("date") &&
                typeof value === "number" &&
                value > 1000000000000
              ) {
                try {
                  value = new Date(value).toISOString().split("T")[0];
                } catch (error) {
                  console.warn(
                    "Table: Error converting timestamp:",
                    value,
                    error
                  );
                }
              }

              dataPoint[cleanKey] = value;
            });
            return dataPoint;
          });
          console.log(
            "Table: Single response - transformed data from array format"
          );
          return transformedData;
        }
      }

      console.log(
        "Table: No valid data structure found in response, returning empty array"
      );
      return [];
    } catch (error) {
      console.error("Table: Error transforming NiFi data:", error);
      return [];
    }
  };

  // Helper function to check if query is bank-only - SAME AS CARD
  const isBankOnlyQuery = (queryObject) => {
    const entity = queryObject.entity || "";
    const joins = queryObject.joins || [];

    const isCoreBank =
      entity.toLowerCase().includes("core.bank") ||
      entity.toLowerCase() === "bank";
    const hasNoJoins = joins.length === 0;

    console.log("Table: Checking if bank-only query:", {
      entity,
      isCoreBank,
      hasNoJoins,
      joins,
    });

    return isCoreBank && hasNoJoins;
  };

  // Fetch filtered data from NiFi - SAME LOGIC AS CARD
  const fetchFilteredData = async (bankIds, branchIds) => {
    try {
      if (!getData || typeof getData !== "function") {
        console.log(
          "Table: getData function not available, skipping NiFi filtering"
        );
        return Array.isArray(data) ? data : [];
      }

      if (!getThemeURL) {
        console.log(
          "Table: getThemeURL not available, skipping NiFi filtering"
        );
        return Array.isArray(data) ? data : [];
      }

      if (!item?.dataSource || !savedQueries || savedQueries.length === 0) {
        console.log("No data source or saved queries available for table");
        return Array.isArray(data) ? data : [];
      }

      const currentDashboardWidgetId = `WIDGET_${item.id}`;
      const matchedQuery = savedQueries.find(
        (q) => q.dashboardWidgetId === currentDashboardWidgetId
      );

      console.log("Table matchedQuery", matchedQuery);
      console.log("Table serviceName", item.dataSource);

      if (!matchedQuery) {
        console.log(
          "No matching query found for table data source:",
          item.dataSource
        );
        return Array.isArray(data) ? data : [];
      }

      const queryObject =
        typeof matchedQuery.queryBuilder === "string"
          ? JSON.parse(matchedQuery.queryBuilder)
          : matchedQuery.queryBuilder;

      // Check if this is a bank-only query
      const isBankOnly = isBankOnlyQuery(queryObject);
      console.log("Table: Is bank-only query:", isBankOnly);

      if (isBankOnly) {
        // BANK-ONLY SCENARIOS
        const hasBankIds = bankIds && bankIds.length > 0;
        const hasBranchIds = branchIds && branchIds.length > 0;

        console.log("Table: Bank-only query scenarios:", {
          hasBankIds,
          hasBranchIds,
          bankIds,
          branchIds,
        });

        // Scenario 2: Only branch IDs are available (no bank IDs)
        if (!hasBankIds && hasBranchIds) {
          console.log(
            "Table: Scenario 2 - Only branch IDs available for bank-only query, returning empty data"
          );
          return [];
        }

        // Scenario 1 & 3: Use only bank IDs
        if (hasBankIds) {
          console.log(
            `Table: Scenario ${
              hasBranchIds ? "3" : "1"
            } - Using bank IDs for bank-only query`
          );

          const filters = {
            "bank.id": {
              operator: "IN",
              value: bankIds,
            },
          };

          const enhancedQuery = {
            ...queryObject,
            filters: {
              ...(queryObject.filters || {}),
              ...filters,
            },
          };

          console.log(
            "Table: Enhanced bank-only query:",
            JSON.stringify(enhancedQuery, null, 2)
          );

          let userResponse;
          try {
            userResponse = await getData(getThemeURL);
          } catch (error) {
            console.error("Table: Error fetching user profile:", error);
            return Array.isArray(data) ? data : [];
          }

          if (
            !userResponse ||
            !Array.isArray(userResponse) ||
            userResponse.length === 0
          ) {
            console.error(
              "Table: Failed to fetch user profile or invalid response"
            );
            return Array.isArray(data) ? data : [];
          }

          const user = userResponse[0];
          const roleName = user.role_name;
          const userBankId = user.bank_id;

          const authObject = {
            instanceUrl: "/query-builder-api/execute",
            apiType: "POST",
          };

          const processBankIds = (ids) => {
            if (!ids) return [];

            if (Array.isArray(ids)) {
              return ids.map((id) => Number(id));
            } else if (typeof ids === "string") {
              return ids.split(",").map((id) => Number(id.trim()));
            } else {
              return [Number(ids)];
            }
          };

          if (roleName === "SUPER ADMIN") {
            authObject.type = "SA";
            authObject.bank_id = processBankIds(instanceBankIds);
          } else if (user.is_admin === true) {
            authObject.type = "HO";
            authObject.bank_id = processBankIds(userBankId);
          } else {
            authObject.type = "U";
            authObject.bank_id = processBankIds(userBankId);
          }

          const payloadWithAuth = {
            auth: authObject,
            main: enhancedQuery,
          };

          setLoading(true);
          setError(null);

          const response = await fetch("/nifi/fetchQuery-API", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payloadWithAuth),
          });

          console.log(
            "Table: Bank-only query payload:",
            JSON.stringify(payloadWithAuth)
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const result = await response.json();
          console.log("Table: Bank-only query result:", result);

          if (result.status === "error") {
            throw new Error(result.message || "Query execution failed");
          }

          const transformedData = transformNiFiDataToTableFormat(result);
          console.log("Table: Transformed bank-only data:", transformedData);

          return transformedData;
        }

        console.log(
          "Table: No bank IDs available for bank-only query, returning empty data"
        );
        return [];
      }

      // NON-BANK-ONLY QUERIES
      else {
        console.log("Table: Processing non-bank-only query");

        const entity = queryObject.entity || "";
        const tableName = entity.includes(".") ? entity.split(".")[1] : entity;

        const joins = queryObject.joins || [];
        const joinedTables = joins.map((join) => {
          const table = join.table || join.alias || "";
          return table.includes(".") ? table.split(".")[1] : table;
        });

        const determineFilterFields = () => {
          let bankFilterField = null;
          let branchFilterField = null;

          console.log("Table: Table analysis:", { tableName, joinedTables });

          // FIRST CHECK: For any table that is NOT "bank" or "branch_details"
          if (tableName !== "bank" && tableName !== "branch_details") {
            bankFilterField = `${tableName}.bank_id`;
            branchFilterField = `${tableName}.branch_id`;
            return { bankFilterField, branchFilterField };
          }

          const hasBankTable =
            tableName === "bank" || joinedTables.includes("bank");
          const hasBranchDetailsTable =
            tableName === "branch_details" ||
            joinedTables.includes("branch_details");
          const hasJoins = joins.length > 0;

          if (tableName === "bank") {
            bankFilterField = "bank.id";
            if (!hasJoins) {
              branchFilterField = null;
              return { bankFilterField, branchFilterField };
            }
          } else if (hasBankTable && hasJoins) {
            bankFilterField = "bank.id";
          } else if (
            !hasBankTable &&
            tableName !== "bank" &&
            tableName !== "branch_details"
          ) {
            bankFilterField = `${tableName}.bank_id`;
          } else if (!hasBankTable) {
            bankFilterField = `${tableName}.bank_id`;
          }

          if (tableName === "branch_details") {
            branchFilterField = "branch_details.id";
          } else if (hasBranchDetailsTable && hasJoins) {
            branchFilterField = "branch_details.id";
          } else if (
            !hasBranchDetailsTable &&
            tableName !== "bank" &&
            tableName !== "branch_details"
          ) {
            branchFilterField = `${tableName}.branch_id`;
          } else {
            const tableWithBranchId = joinedTables.find(
              (table) => table !== "bank" && table !== "branch_details"
            );
            if (tableWithBranchId) {
              branchFilterField = `${tableWithBranchId}.branch_id`;
            } else {
              branchFilterField = `${tableName}.branch_id`;
            }
          }

          return { bankFilterField, branchFilterField };
        };

        const { bankFilterField, branchFilterField } = determineFilterFields();

        console.log("Table: Determined filter fields:", {
          bankFilterField,
          branchFilterField,
        });

        const filters = {};

        if (bankIds && bankIds.length > 0 && bankFilterField) {
          filters[bankFilterField] = {
            operator: "IN",
            value: bankIds,
          };
        }

        if (branchIds && branchIds.length > 0 && branchFilterField) {
          filters[branchFilterField] = {
            operator: "IN",
            value: branchIds,
          };
        } else if (!branchIds || branchIds.length === 0) {
          if (branchFilterField) {
            filters[branchFilterField] = null;
          }
        }

        const enhancedQuery = {
          ...queryObject,
          filters: {
            ...(queryObject.filters || {}),
            ...filters,
          },
        };

        console.log(
          "Table: Enhanced non-bank-only query:",
          JSON.stringify(enhancedQuery, null, 2)
        );

        let userResponse;
        try {
          userResponse = await getData(getThemeURL);
        } catch (error) {
          console.error("Table: Error fetching user profile:", error);
          return Array.isArray(data) ? data : [];
        }

        if (
          !userResponse ||
          !Array.isArray(userResponse) ||
          userResponse.length === 0
        ) {
          console.error(
            "Table: Failed to fetch user profile or invalid response"
          );
          return Array.isArray(data) ? data : [];
        }

        const user = userResponse[0];
        const roleName = user.role_name;
        const userBankId = user.bank_id;

        const authObject = {
          instanceUrl: "/query-builder-api/execute",
          apiType: "POST",
        };

        const processBankIds = (ids) => {
          if (!ids) return [];

          if (Array.isArray(ids)) {
            return ids.map((id) => Number(id));
          } else if (typeof ids === "string") {
            return ids.split(",").map((id) => Number(id.trim()));
          } else {
            return [Number(ids)];
          }
        };

        if (roleName === "SUPER ADMIN") {
          authObject.type = "SA";
          authObject.bank_id = processBankIds(instanceBankIds);
        } else if (user.is_admin === true) {
          authObject.type = "HO";
          authObject.bank_id = processBankIds(userBankId);
        } else {
          authObject.type = "U";
          authObject.bank_id = processBankIds(userBankId);
        }

        const payloadWithAuth = {
          auth: authObject,
          main: enhancedQuery,
        };

        setLoading(true);
        setError(null);

        const response = await fetch("/nifi/fetchQuery-API", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payloadWithAuth),
        });

        console.log(
          "Table: Non-bank-only query payload:",
          JSON.stringify(payloadWithAuth)
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        console.log("Table: Non-bank-only query result:", result);

        if (result.status === "error") {
          throw new Error(result.message || "Query execution failed");
        }

        const transformedData = transformNiFiDataToTableFormat(result);
        console.log("Table: Final transformed data:", transformedData);

        return transformedData;
      }
    } catch (error) {
      console.error("Table: Error fetching filtered data:", error);
      setError(`Failed to fetch filtered data: ${error.message}`);
      return Array.isArray(data) ? data : [];
    } finally {
      setLoading(false);
    }
  };

  // Effect to handle data fetching when filters change
  useEffect(() => {
    const loadData = async () => {
      console.log("Table LoadData called with:", {
        selectedBankIds,
        selectedBranchIds,
        hasDataSource: !!item?.dataSource,
        currentDataLength: tableData.length,
        savedQueriesLength: savedQueries?.length || 0,
      });

      if (loadingQueries) {
        console.log("Still loading queries, skipping table data fetch");
        return;
      }

      if (!data || !Array.isArray(data)) {
        setTableData([]);
        return;
      }

      try {
        let resultData;

        if (
          item?.dataSource &&
          savedQueries &&
          savedQueries.length > 0 &&
          getData &&
          getThemeURL
        ) {
          console.log("Table: Attempting NiFi filtering:", {
            dataSource: item.dataSource,
            selectedBankIds,
            selectedBranchIds,
          });

          resultData = await fetchFilteredData(
            selectedBankIds,
            selectedBranchIds
          );
        } else {
          console.log(
            "Table: No NiFi dataSource or missing functions, using local filtering only"
          );

          let filteredData = data;
          const hasBankId = filteredData.some(
            (entry) =>
              entry.hasOwnProperty("bank_id") ||
              entry.hasOwnProperty("bankId") ||
              entry.hasOwnProperty("Bank_ID")
          );

          const hasBranchId = filteredData.some(
            (entry) =>
              entry.hasOwnProperty("branchdetails_id") ||
              entry.hasOwnProperty("branch_id") ||
              entry.hasOwnProperty("branchId") ||
              entry.hasOwnProperty("Branch_ID")
          );

          if (
            selectedBranchIds &&
            selectedBranchIds.length > 0 &&
            hasBranchId
          ) {
            filteredData = filteredData.filter((entry) => {
              const entryBranchId =
                entry.branchdetails_id ||
                entry.branch_id ||
                entry.branchId ||
                entry.Branch_ID;
              return selectedBranchIds.includes(entryBranchId);
            });
          } else if (
            selectedBankIds &&
            selectedBankIds.length > 0 &&
            hasBankId
          ) {
            filteredData = filteredData.filter((entry) => {
              const entryBankId =
                entry.bank_id || entry.bankId || entry.Bank_ID;
              const entryBranchId =
                entry.branchdetails_id ||
                entry.branch_id ||
                entry.branchId ||
                entry.Branch_ID;

              const recordHasBranchId =
                entryBranchId !== undefined &&
                entryBranchId !== null &&
                entryBranchId !== "";

              if (recordHasBranchId) {
                return false;
              }

              return selectedBankIds.includes(entryBankId);
            });
          }

          resultData = filteredData;
        }

        setTableData([...resultData]);
      } catch (error) {
        console.error("Error loading table data:", error);
        setTableData([...(data || [])]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [
    selectedBankIds,
    selectedBranchIds,
    data,
    item?.dataSource,
    savedQueries,
    loadingQueries,
    getData,
    getThemeURL,
    instanceBankIds,
  ]);

  // Debug useEffect
  useEffect(() => {
    console.log("Table data state updated:", {
      length: tableData.length,
      data: tableData,
      timestamp: new Date().toISOString(),
    });
  }, [tableData]);

  // Toggle row expansion
  const toggleRow = (rowId) => {
    setExpandedRows((prev) => ({ ...prev, [rowId]: !prev[rowId] }));
  };

  // Apply date filter
  const applyDateFilter = () => {
    setAppliedDateRange({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };

  // Reset date filter
  const resetDateFilter = () => {
    setDateRange({ startDate: null, endDate: null });
    setAppliedDateRange({ startDate: null, endDate: null });
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Generate pagination options
  const generatePaginationOptions = (totalRows) => {
    const baseOptions = [5, 10, 20, 50];

    if (totalRows > 50) baseOptions.push(100);
    if (totalRows > 100) baseOptions.push(250);
    if (totalRows > 250) baseOptions.push(500);
    if (totalRows > 500) baseOptions.push(1000);
    if (totalRows > 1000 && totalRows <= 5000) {
      baseOptions.push(2500);
      baseOptions.push(totalRows);
    } else if (totalRows > 5000) {
      baseOptions.push(2500, 5000);
    } else if (totalRows > 5) {
      baseOptions.push(totalRows);
    }

    return [...new Set(baseOptions)]
      .filter((option) => option <= totalRows)
      .sort((a, b) => a - b);
  };

  // Format column labels
  const formatColumnLabel = (columnName) => {
    if (!columnName) return "Date Field";

    const cleanName = columnName.includes(".")
      ? columnName.split(".").pop()
      : columnName;

    return cleanName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .replace(/_/g, " ");
  };

  const getButtonStyle = (baseColor) => ({
    padding: showSearch ? "8px 12px" : "10px 16px", // More padding when no search
    backgroundColor: baseColor,
    color: "white",
    border: "none",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    fontWeight: "500",
    height: showSearch ? "36px" : "36px", // Slightly taller when no search
    minWidth: showSearch ? "50px" : "50px", // Consistent minimum width
  });

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Get badge color
  const getBadgeColor = (value) => {
    if (value <= 5) return "#10B981";
    if (value <= 10) return "#F59E0B";
    return "#EF4444";
  };
  console.log("data");
  // EARLY RETURN: Check for no initial data BEFORE any processing
  // if (!data || !Array.isArray(data) || data.length === 0) {
  //   return (
  //     <div
  //       style={{
  //          display: "flex",
  //         alignItems: "center",
  //         justifyContent: "center",
  //         height: "200px",
  //         color: "#6b7280",
  //         fontSize: "14px",
  //         textAlign: "center",
  //         padding: "20px",
  //         flexDirection: "column",
  //         gap: "12px",
  //       }}
  //     >
  //       <div
  //         style={{
  //           padding: "16px",
  //           backgroundColor: "#fef3c7",
  //           borderRadius: "8px",
  //           border: "1px solid #f59e0b",
  //         }}
  //       >
  //         <div
  //           style={{
  //             color: "#92400e",
  //             fontWeight: "600",
  //             marginBottom: "4px",
  //           }}
  //         >
  //           No Data Available
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // Show loading state for queries or data
  if (loading || loadingQueries) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#6b7280",
          fontSize: "14px",
        }}
      >
        {loadingQueries ? "Loading..." : "Loading table data..."}
      </div>
    );
  }

  // Show error state if queries failed to load
  if (queryError) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#ef4444",
          fontSize: "14px",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #ef4444",
          }}
        >
          <div style={{ fontWeight: "600", marginBottom: "4px" }}>
            Error Loading Queries
          </div>
          <div style={{ fontSize: "12px" }}>{queryError}</div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#ef4444",
          fontSize: "14px",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <div>Error: {error}</div>
      </div>
    );
  }

  const currentPage = tablePagination || 1;
  const currentRowsPerPage = tableRowsPerPage || 5;

  const dateColumn = selectedDateField;

  const headerStyle = {
    backgroundColor: item.headerColor || "#f9fafb",
    color: item.headerTextColor || "#374151",
  };

  const subHeaderStyle = {
    backgroundColor: item.subHeaderColor || "#f1f5f9",
    color: item.subHeaderTextColor || "#334155",
  };

  const visibleColumns = columns
    .filter((c) => c.selected)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map((col) => ({
      ...col,
      label:
        col.label ||
        col.key
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase()),
    }));

  const visibleSubColumns =
    tableType === "expandable"
      ? subColumns
          .filter((c) => c.selected)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
          .map((col) => ({
            ...col,
            label:
              col.label ||
              col.key
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase()),
          }))
      : [];

  // Apply date filtering
  let filteredData = [...tableData];
  if (
    showDateFilter &&
    dateColumn &&
    (appliedDateRange.startDate || appliedDateRange.endDate)
  ) {
    filteredData = tableData.filter((row) => {
      let dateValue;
      if (dateColumn.includes(".")) {
        const fieldName = dateColumn.split(".").pop();
        dateValue = row[fieldName];
      } else {
        dateValue = row[dateColumn];
      }

      if (!dateValue) return false;

      try {
        const rowDate = new Date(dateValue);
        if (isNaN(rowDate.getTime())) return false;

        const startValid =
          !appliedDateRange.startDate ||
          rowDate >= new Date(appliedDateRange.startDate);
        const endValid =
          !appliedDateRange.endDate ||
          rowDate <=
            new Date(
              new Date(appliedDateRange.endDate).setHours(23, 59, 59, 999)
            );
        return startValid && endValid;
      } catch (error) {
        console.error("Error parsing date for filtering:", error);
        return false;
      }
    });
  }

  // Apply search filtering
  if (searchTerm && showSearch) {
    filteredData = filteredData.filter((row) =>
      columns.some((column) => {
        const value = String(row[column.key] || "").toLowerCase();
        return value.includes(searchTerm.toLowerCase());
      })
    );
  }

  // Apply sorting
  let sortedData = [...filteredData];
  if (enableSorting && sortConfig.key) {
    sortedData.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  }

  // Calculate pagination
  const totalRows = sortedData.length;
  const indexOfLastRow = currentPage * currentRowsPerPage;
  const indexOfFirstRow = indexOfLastRow - currentRowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(totalRows / currentRowsPerPage);

  // Show no data message when filters are applied but no data matches
  console.log("tableData", tableData.length);
  if (tableData.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#6b7280",
          fontSize: "14px",
          textAlign: "center",
          padding: "20px",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fef3c7",
            borderRadius: "8px",
            border: "1px solid #f59e0b",
          }}
        >
          <div
            style={{
              color: "#92400e",
              fontWeight: "600",
              marginBottom: "4px",
            }}
          >
            No Data Available
          </div>
        </div>
      </div>
    );
  }

  const downloadPDF = async () => {
    try {
      const doc = new jsPDF("l", "pt", "a4");
      let currentY = 50;

      let fontName = "Helvetica";
      doc.setFont(fontName);
      doc.setFontSize(13);

      const pdfTitle =
        item.pdfTitle !== undefined &&
        item.pdfTitle !== null &&
        item.pdfTitle.trim() !== ""
          ? item.pdfTitle.trim()
          : "";

      if (item.showBankLogo && bankName) {
        try {
          const pageWidth = doc.internal.pageSize.width;
          const centerX = pageWidth / 2;

          doc.setFont(fontName, "bold");
          const bankNameFontSize =
            bankData?.banknameFontsize || bankData?.branchNameFontsize || 16;
          doc.setFontSize(parseInt(bankNameFontSize));

          if (logoFromApi) {
            // Logo exists - show logo + name side by side
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();
            img.crossOrigin = "anonymous";

            await new Promise((resolve, reject) => {
              img.onload = () => {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                resolve();
              };
              img.onerror = reject;
              img.src = logoFromApi;
            });

            const logoDataURL = canvas.toDataURL("image/png");
            const logoWidth = 70;
            const logoHeight = 70;
            const textWidth = doc.getTextWidth(bankName);
            const spacing = 15;
            const totalWidth = logoWidth + spacing + textWidth;
            const startX = centerX - totalWidth / 2;

            doc.addImage(
              logoDataURL,
              "PNG",
              startX,
              currentY,
              logoWidth,
              logoHeight
            );
            doc.text(
              bankName,
              startX + logoWidth + spacing,
              currentY + logoHeight / 2 + 6
            );

            currentY += Math.max(logoHeight, 40) + 20;
          } else {
            // No logo - just show centered bank name
            doc.text(bankName, centerX, currentY, { align: "center" });
            currentY += 30;
          }

          doc.setFont(fontName, "normal");
          doc.setFontSize(13);
        } catch (error) {
          console.error("Error adding bank info to PDF:", error);
        }
      }

      const xOffset = doc.internal.pageSize.width / 2;
      doc.text(pdfTitle, xOffset, currentY, { align: "center" });
      currentY += 30;

      const pageWidth = doc.internal.pageSize.width;
      const leftMargin = 40;
      const rightMargin = 40;
      const availableWidth = pageWidth - leftMargin - rightMargin;

      const isExpandableTable =
        tableType === "expandable" &&
        visibleSubColumns &&
        visibleSubColumns.length > 0;

      if (isExpandableTable) {
        const mainHeaders = visibleColumns.map((col) => col.label);
        const subHeaders = visibleSubColumns.map((col) => col.label);
        const combinedHeaders = [...mainHeaders, ...subHeaders];
        const totalColumns = combinedHeaders.length;

        let columnWidth = availableWidth / totalColumns;
        let fontSize = 8;
        let tableMargin = leftMargin;

        if (totalColumns <= 5) {
          columnWidth = availableWidth / totalColumns;
          tableMargin = leftMargin;
          fontSize = 10;
        } else if (totalColumns > 10) {
          fontSize = 7;
          columnWidth = Math.max(50, columnWidth);
        }

        const combinedData = [];
        sortedData.forEach((row) => {
          const combinedRow = [];

          visibleColumns.forEach((col) => {
            let cellValue = row[col.key];

            if (typeof cellValue === "boolean") {
              cellValue = cellValue ? "Yes" : "No";
            } else if (
              col.type === "date" ||
              (typeof cellValue === "string" &&
                (/^\d{4}-\d{2}-\d{2}/.test(cellValue) ||
                  cellValue.includes("T")))
            ) {
              try {
                let dateValue = cellValue;

                if (typeof cellValue === "string" && cellValue.includes("T")) {
                  dateValue = cellValue.split("T")[0];
                }

                if (
                  typeof cellValue === "string" &&
                  cellValue.includes(" ") &&
                  !cellValue.includes("T")
                ) {
                  dateValue = cellValue.split(" ")[0];
                }

                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  const day = date.getDate().toString().padStart(2, "0");
                  const month = (date.getMonth() + 1)
                    .toString()
                    .padStart(2, "0");
                  const year = date.getFullYear();
                  cellValue = `${day}-${month}-${year}`;
                }
              } catch (error) {
                console.error("PDF date formatting error:", error);
              }
            }

            if (
              cellValue === null ||
              cellValue === undefined ||
              String(cellValue).trim() === ""
            ) {
              cellValue = "N/A";
            }

            combinedRow.push(String(cellValue));
          });

          visibleSubColumns.forEach((col) => {
            let cellValue = row[col.key];

            if (typeof cellValue === "boolean") {
              cellValue = cellValue ? "Yes" : "No";
            } else if (
              col.type === "date" ||
              (typeof cellValue === "string" &&
                (/^\d{4}-\d{2}-\d{2}/.test(cellValue) ||
                  cellValue.includes("T")))
            ) {
              try {
                let dateValue = cellValue;

                if (typeof cellValue === "string" && cellValue.includes("T")) {
                  dateValue = cellValue.split("T")[0];
                }

                if (
                  typeof cellValue === "string" &&
                  cellValue.includes(" ") &&
                  !cellValue.includes("T")
                ) {
                  dateValue = cellValue.split(" ")[0];
                }

                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  const day = date.getDate().toString().padStart(2, "0");
                  const month = (date.getMonth() + 1)
                    .toString()
                    .padStart(2, "0");
                  const year = date.getFullYear();
                  cellValue = `${day}-${month}-${year}`;
                }
              } catch (error) {
                console.error("PDF date formatting error:", error);
              }
            }

            if (
              cellValue === null ||
              cellValue === undefined ||
              String(cellValue).trim() === ""
            ) {
              cellValue = "N/A";
            }

            combinedRow.push(String(cellValue));
          });

          combinedData.push(combinedRow);
        });

        const combinedColumnStyles = {};
        combinedHeaders.forEach((header, index) => {
          combinedColumnStyles[index] = {
            cellWidth: columnWidth,
            fontSize: fontSize,
          };
        });

        doc.autoTable({
          head: [combinedHeaders],
          body: combinedData,
          startY: currentY,
          theme: "grid",
          headStyles: {
            fillColor: [59, 130, 246],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "center",
            fontSize: fontSize,
          },
          bodyStyles: {
            fontSize: fontSize,
          },
          columnStyles: combinedColumnStyles,
          margin: {
            top: currentY,
            left: tableMargin,
            right: rightMargin,
            bottom: 40,
          },
          tableWidth: "wrap",
          didParseCell: function (data) {
            if (data.section === "body") {
              const cellValue = data.cell.raw;

              if (
                typeof cellValue === "number" ||
                (typeof cellValue === "string" &&
                  !isNaN(cellValue) &&
                  !isNaN(parseFloat(cellValue)) &&
                  cellValue.trim() !== "")
              ) {
                data.cell.styles.halign = "right";
              } else {
                data.cell.styles.halign = "left";
              }
            }
          },
        });
      } else {
        const tableHeaders = visibleColumns.map((col) => col.label);
        const totalColumns = visibleColumns.length;

        let columnWidth = availableWidth / totalColumns;
        let fontSize = 10;
        let tableMargin = leftMargin;

        if (totalColumns <= 5) {
          columnWidth = availableWidth / totalColumns;
          tableMargin = leftMargin;
          fontSize = 11;
        } else if (totalColumns > 8) {
          fontSize = 8;
          columnWidth = Math.max(60, columnWidth);
        }

        const tableData = [];

        sortedData.forEach((row) => {
          const rowData = [];
          visibleColumns.forEach((col) => {
            let cellValue = row[col.key];

            if (typeof cellValue === "boolean") {
              cellValue = cellValue ? "Yes" : "No";
            } else if (
              col.type === "date" ||
              (typeof cellValue === "string" &&
                (/^\d{4}-\d{2}-\d{2}/.test(cellValue) ||
                  cellValue.includes("T")))
            ) {
              try {
                let dateValue = cellValue;

                if (typeof cellValue === "string" && cellValue.includes("T")) {
                  dateValue = cellValue.split("T")[0];
                }

                if (
                  typeof cellValue === "string" &&
                  cellValue.includes(" ") &&
                  !cellValue.includes("T")
                ) {
                  dateValue = cellValue.split(" ")[0];
                }

                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  const day = date.getDate().toString().padStart(2, "0");
                  const month = (date.getMonth() + 1)
                    .toString()
                    .padStart(2, "0");
                  const year = date.getFullYear();
                  cellValue = `${day}-${month}-${year}`;
                }
              } catch (error) {
                console.error("PDF date formatting error:", error);
              }
            }

            if (
              cellValue === null ||
              cellValue === undefined ||
              String(cellValue).trim() === ""
            ) {
              cellValue = "N/A";
            }

            rowData.push(String(cellValue));
          });
          tableData.push(rowData);
        });

        const columnStyles = {};
        visibleColumns.forEach((col, index) => {
          columnStyles[index] = {
            cellWidth: columnWidth,
            fontSize: fontSize,
          };
        });

        doc.autoTable({
          head: [tableHeaders],
          body: tableData,
          startY: currentY,
          theme: "grid",
          headStyles: {
            fillColor: [59, 130, 246],
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "center",
            fontSize: fontSize,
          },
          bodyStyles: {
            fontSize: fontSize,
          },
          columnStyles: columnStyles,
          margin: {
            top: currentY,
            left: tableMargin,
            right: rightMargin,
            bottom: 40,
          },
          tableWidth: "wrap",
          didParseCell: function (data) {
            if (data.section === "body") {
              const cellValue = data.cell.raw;

              if (
                typeof cellValue === "number" ||
                (typeof cellValue === "string" &&
                  !isNaN(cellValue) &&
                  !isNaN(parseFloat(cellValue)) &&
                  cellValue.trim() !== "")
              ) {
                data.cell.styles.halign = "right";
              } else {
                data.cell.styles.halign = "left";
              }
              data.cell.styles.fontSize = fontSize;
            }
          },
        });
      }

      // **UPDATED FOOTER SECTION WITH PAGE BREAK LOGIC**
      const pageHeight = doc.internal.pageSize.height;
      const footerHeight = 180; // Total height needed for footer
      const finalY = doc.lastAutoTable.finalY || currentY; // Get where table ended

      // Check if there's enough space for footer on current page
      if (finalY + footerHeight > pageHeight) {
        // Not enough space, add new page
        doc.addPage();
        currentY = 50; // Reset Y position for new page
      } else {
        // Use existing page
        currentY = finalY;
      }

      // Position footer at bottom of page
      let footerY = pageHeight - 180;

      doc.setFontSize(10);

      doc.text("Letter No. : ", 50, footerY + 100);
      doc.text("Date : ", 50, footerY + 120);
      doc.text("Copy : ", 50, footerY + 140);

      doc.text("XXXX", 550, footerY + 100);
      doc.text("XXXXXXXXXX", 550, footerY + 120);
      doc.text("XXXXXX", 550, footerY + 140);

      doc.setFont(fontName, "bold");
      doc.text("[ ]", 550, footerY + 160);
      doc.setFont(fontName, "normal");

      const now = new Date();
      const istDate = now.toISOString().split("T")[0].replace(/-/g, "");
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const seconds = now.getSeconds().toString().padStart(2, "0");
      const istTime = `${hours}${minutes}${seconds}`;
      const fileTimestamp = `${istDate}_${istTime}`;
      const fileTitle = title.replace(/\s+/g, "_").toLowerCase();
      const filename = `${fileTitle}_${fileTimestamp}.pdf`;

      doc.save(filename);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };

  const downloadExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Data");

      let currentRow = 1;

      if (item.showBankLogo && bankName) {
        try {
          if (logoFromApi) {
            // Logo exists - add logo and name
            const response = await fetch(logoFromApi);
            const arrayBuffer = await response.arrayBuffer();

            const imageId = workbook.addImage({
              buffer: arrayBuffer,
              extension: "png",
            });

            worksheet.addImage(imageId, {
              tl: { col: 0, row: 0 },
              ext: { width: 100, height: 100 },
            });

            const bankNameCell = worksheet.getCell("B1");
            bankNameCell.value = bankName;
            bankNameCell.font = {
              bold: true,
              size: parseInt(
                bankData?.banknameFontsize || bankData?.branchNameFontsize || 16
              ),
            };
            bankNameCell.alignment = { vertical: "middle", horizontal: "left" };

            worksheet.getRow(1).height = 75;
            worksheet.getRow(2).height = 25;
            currentRow = 4;
          } else {
            // No logo - just add centered bank name
            const bankNameCell = worksheet.getCell("A1");
            bankNameCell.value = bankName;
            bankNameCell.font = {
              bold: true,
              size: parseInt(
                bankData?.banknameFontsize || bankData?.branchNameFontsize || 16
              ),
            };
            bankNameCell.alignment = {
              vertical: "middle",
              horizontal: "center",
            };

            // Merge cells across all columns for centered text
            const totalColumns =
              visibleColumns.length +
              (tableType === "expandable" && visibleSubColumns.length > 0
                ? visibleSubColumns.length
                : 0);
            worksheet.mergeCells(
              `A1:${String.fromCharCode(64 + totalColumns)}1`
            );

            worksheet.getRow(1).height = 30;
            worksheet.getRow(2).height = 15;
            currentRow = 3;
          }
        } catch (error) {
          console.error("Error adding bank info to Excel:", error);
          currentRow = 1;
        }
      }

      const titleText =
        item.pdfTitle !== undefined &&
        item.pdfTitle !== null &&
        item.pdfTitle.trim() !== ""
          ? item.pdfTitle.trim()
          : null;

      if (titleText) {
        worksheet.getCell(`A${currentRow}`).value = titleText;

        const titleRow = worksheet.getRow(currentRow);
        titleRow.font = { bold: true, size: 16 };
        titleRow.alignment = { horizontal: "center" };

        const totalColumns =
          visibleColumns.length +
          (tableType === "expandable" && visibleSubColumns.length > 0
            ? visibleSubColumns.length
            : 0);
        worksheet.mergeCells(
          `A${currentRow}`,
          `${String.fromCharCode(64 + totalColumns)}${currentRow}`
        );

        currentRow += 2;
      }

      const headerRow = visibleColumns.map((col) => col.label);
      if (tableType === "expandable" && visibleSubColumns.length > 0) {
        headerRow.push(...visibleSubColumns.map((col) => col.label));
      }
      worksheet.addRow(headerRow);

      const headerRowIndex = currentRow;
      currentRow++;

      const headerRowObj = worksheet.getRow(headerRowIndex);
      headerRowObj.font = { bold: true };
      headerRowObj.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };

      if (!sortedData || sortedData.length === 0) {
        const naRow = new Array(headerRow.length).fill("N/A");
        worksheet.addRow(naRow);
      } else {
        sortedData.forEach((row, rowIndex) => {
          const dataRow = [];

          visibleColumns.forEach((col, colIndex) => {
            const cellValue = row[col.key];
            let processedValue = cellValue;

            if (typeof cellValue === "boolean") {
              processedValue = cellValue ? "Yes" : "No";
            } else {
              const isDateField =
                col.type === "date" ||
                (typeof cellValue === "string" &&
                  (/^\d{4}-\d{2}-\d{2}/.test(cellValue) ||
                    cellValue.includes("T")));

              if (isDateField && cellValue) {
                try {
                  let dateValue = cellValue;

                  if (
                    typeof cellValue === "string" &&
                    cellValue.includes("T")
                  ) {
                    dateValue = cellValue.split("T")[0];
                  }

                  if (
                    typeof cellValue === "string" &&
                    cellValue.includes(" ") &&
                    !cellValue.includes("T")
                  ) {
                    dateValue = cellValue.split(" ")[0];
                  }

                  const date = new Date(dateValue);
                  if (!isNaN(date.getTime())) {
                    const day = date.getDate().toString().padStart(2, "0");
                    const month = (date.getMonth() + 1)
                      .toString()
                      .padStart(2, "0");
                    const year = date.getFullYear();
                    processedValue = `${day}-${month}-${year}`;
                  }
                } catch (error) {
                  console.error("Excel date formatting error:", error);
                  processedValue = cellValue;
                }
              }
            }

            dataRow.push(
              processedValue === null ||
                processedValue === undefined ||
                String(processedValue).trim() === ""
                ? "N/A"
                : processedValue
            );
          });

          if (tableType === "expandable") {
            visibleSubColumns.forEach((col) => {
              const cellValue = row[col.key];
              let processedValue = cellValue;

              if (typeof cellValue === "boolean") {
                processedValue = cellValue ? "Yes" : "No";
              } else {
                const isDateField =
                  col.type === "date" ||
                  (typeof cellValue === "string" &&
                    (/^\d{4}-\d{2}-\d{2}/.test(cellValue) ||
                      cellValue.includes("T")));

                if (isDateField && cellValue) {
                  try {
                    let dateValue = cellValue;

                    if (
                      typeof cellValue === "string" &&
                      cellValue.includes("T")
                    ) {
                      dateValue = cellValue.split("T")[0];
                    }

                    if (
                      typeof cellValue === "string" &&
                      cellValue.includes(" ") &&
                      !cellValue.includes("T")
                    ) {
                      dateValue = cellValue.split(" ")[0];
                    }

                    const date = new Date(dateValue);
                    if (!isNaN(date.getTime())) {
                      const day = date.getDate().toString().padStart(2, "0");
                      const month = (date.getMonth() + 1)
                        .toString()
                        .padStart(2, "0");
                      const year = date.getFullYear();
                      processedValue = `${day}-${month}-${year}`;
                    }
                  } catch (error) {
                    console.error("Excel date formatting error:", error);
                    processedValue = cellValue;
                  }
                }
              }

              dataRow.push(
                processedValue === null ||
                  processedValue === undefined ||
                  String(processedValue).trim() === ""
                  ? "N/A"
                  : processedValue
              );
            });
          }

          worksheet.addRow(dataRow);
        });
      }

      worksheet.columns.forEach((column) => {
        column.width = 15;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const now = new Date();
      const istDate = now.toISOString().split("T")[0].replace(/-/g, "");
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const seconds = now.getSeconds().toString().padStart(2, "0");
      const istTime = `${hours}${minutes}${seconds}`;
      const fileTimestamp = `${istDate}_${istTime}`;
      const fileTitle = title.replace(/\s+/g, "_").toLowerCase();
      const filename = `${fileTitle}_${fileTimestamp}.xlsx`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating Excel:", error);
      alert("Error generating Excel file. Please try again.");
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowX: "auto",
      }}
    >
      {/* Date Filter Section */}
      {showDateFilter && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            gap: "16px",
            marginBottom: "16px",
            alignItems: "flex-start",
            padding: "12px",
            backgroundColor: "#f8fafc",
            borderRadius: "8px",
            flexWrap: "wrap",
            position: "relative",
            zIndex: 25,
          }}
        >
          <div
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              alignSelf: "center",
              minWidth: "fit-content",
              marginTop: "-7px",
            }}
          >
            Filter by {formatColumnLabel(selectedDateField)}:
          </div>

          {/* From Date Section */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <label style={{ fontSize: "14px", color: "#4b5563" }}>
                From:
              </label>
              <input
                type="date"
                value={
                  dateRange.startDate
                    ? dateRange.startDate.toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = new Date().toISOString().split("T")[0];

                  if (selectedDate > today) {
                    const todayDate = new Date(today);
                    let updatedRange = {
                      startDate: todayDate,
                      tableFromDateError:
                        "Future dates are not allowed. Date has been set to today.",
                    };

                    if (
                      dateRange.endDate &&
                      today > dateRange.endDate.toISOString().split("T")[0]
                    ) {
                      updatedRange.endDate = null;
                      updatedRange.tableToDateError =
                        "To Date cleared because From Date was adjusted to today.";
                    } else {
                      updatedRange.endDate = dateRange.endDate;
                    }

                    setDateRange(updatedRange);

                    setTimeout(() => {
                      setDateRange((prev) => ({
                        ...prev,
                        tableFromDateError: null,
                      }));
                    }, 3000);

                    return;
                  }

                  const newStartDate = selectedDate
                    ? new Date(selectedDate)
                    : null;
                  let updatedRange = {
                    startDate: newStartDate,
                    tableFromDateError: null,
                  };

                  if (
                    newStartDate &&
                    dateRange.endDate &&
                    newStartDate > dateRange.endDate
                  ) {
                    updatedRange.endDate = null;
                    updatedRange.tableToDateError =
                      "To Date cleared because it was earlier than From Date.";

                    setTimeout(() => {
                      setDateRange((prev) => ({
                        ...prev,
                        tableToDateError: null,
                      }));
                    }, 3000);
                  } else {
                    updatedRange.endDate = dateRange.endDate;
                  }

                  setDateRange(updatedRange);
                }}
                max={new Date().toISOString().split("T")[0]}
                style={{
                  width: "140px",
                  padding: "14px 8px",
                  borderRadius: "6px",
                  border: dateRange.tableFromDateError
                    ? "1px solid #ef4444"
                    : "1px solid #d1d5db",
                  fontSize: "13px",
                  height: "30px",
                  lineHeight: "1",
                  boxSizing: "border-box",
                  paddingBottom: "13px",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
                onBlur={(e) =>
                  (e.target.style.borderColor = dateRange.tableFromDateError
                    ? "#ef4444"
                    : "#d1d5db")
                }
              />
            </div>
            {dateRange.tableFromDateError && (
              <span
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  maxWidth: "200px",
                  lineHeight: "1.2",
                  marginLeft: "40px",
                }}
              >
                {dateRange.tableFromDateError}
              </span>
            )}
          </div>

          {/* To Date Section */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <label style={{ fontSize: "14px", color: "#4b5563" }}>To:</label>
              <input
                type="date"
                value={
                  dateRange.endDate
                    ? dateRange.endDate.toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = new Date().toISOString().split("T")[0];

                  if (selectedDate > today) {
                    setDateRange({
                      ...dateRange,
                      endDate: new Date(today),
                      tableToDateError:
                        "Future dates are not allowed. Date has been set to today.",
                    });

                    setTimeout(() => {
                      setDateRange((prev) => ({
                        ...prev,
                        tableToDateError: null,
                      }));
                    }, 3000);

                    return;
                  }

                  const newEndDate = selectedDate
                    ? new Date(selectedDate)
                    : null;

                  if (
                    newEndDate &&
                    dateRange.startDate &&
                    newEndDate < dateRange.startDate
                  ) {
                    setDateRange({
                      ...dateRange,
                      endDate: newEndDate,
                      tableToDateError:
                        "To Date cannot be earlier than From Date.",
                    });

                    return;
                  }

                  setDateRange({
                    ...dateRange,
                    endDate: newEndDate,
                    tableToDateError: null,
                    tableFromDateError: null,
                  });
                }}
                max={new Date().toISOString().split("T")[0]}
                min={
                  dateRange.startDate
                    ? dateRange.startDate.toISOString().split("T")[0]
                    : ""
                }
                style={{
                  width: "140px",
                  padding: "14px 8px",
                  borderRadius: "6px",
                  border: dateRange.tableToDateError
                    ? "1px solid #ef4444"
                    : "1px solid #d1d5db",
                  fontSize: "13px",
                  height: "30px",
                  lineHeight: "1",
                  boxSizing: "border-box",
                  paddingBottom: "13px",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
                onBlur={(e) =>
                  (e.target.style.borderColor = dateRange.tableToDateError
                    ? "#ef4444"
                    : "#d1d5db")
                }
              />
            </div>
            {dateRange.tableToDateError && (
              <span
                style={{
                  color: "#ef4444",
                  fontSize: "12px",
                  maxWidth: "200px",
                  lineHeight: "1.2",
                  marginLeft: "25px",
                }}
              >
                {dateRange.tableToDateError}
              </span>
            )}
          </div>

          {/* Buttons */}
          <button
            onClick={applyDateFilter}
            disabled={
              dateRange.tableFromDateError ||
              dateRange.tableToDateError ||
              (dateRange.startDate && !dateRange.endDate) ||
              (dateRange.startDate &&
                dateRange.endDate &&
                dateRange.startDate > dateRange.endDate)
            }
            style={{
              padding: "8px 16px",
              backgroundColor:
                dateRange.tableFromDateError ||
                dateRange.tableToDateError ||
                (dateRange.startDate && !dateRange.endDate) ||
                (dateRange.startDate &&
                  dateRange.endDate &&
                  dateRange.startDate > dateRange.endDate)
                  ? "#9ca3af"
                  : "#1a7a73",
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor:
                dateRange.tableFromDateError ||
                dateRange.tableToDateError ||
                (dateRange.startDate && !dateRange.endDate) ||
                (dateRange.startDate &&
                  dateRange.endDate &&
                  dateRange.startDate > dateRange.endDate)
                  ? "not-allowed"
                  : "pointer",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              opacity:
                dateRange.startDate &&
                dateRange.endDate &&
                dateRange.startDate > dateRange.endDate
                  ? 0.6
                  : 1,
              height: "fit-content",
              alignSelf: "flex-start",
            }}
          >
            Apply
          </button>

          <button
            onClick={() => {
              resetDateFilter();
              setDateRange((prev) => ({
                ...prev,
                tableFromDateError: null,
                tableToDateError: null,
              }));
            }}
            style={{
              padding: "8px 16px",
              backgroundColor: "#f3f4f6",
              color: "#4b5563",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              height: "fit-content",
              alignSelf: "flex-start",
            }}
          >
            <RotateCcw size={16} />
            Reset
          </button>
        </div>
      )}

      {/* Search/Download Section */}
      {(showSearch || showDownload || showPDFDownload) && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            justifyContent: showSearch ? "space-between" : "flex-end", // Conditional alignment
            alignItems: "center", // Add vertical alignment
            marginBottom: "16px",
            gap: showSearch ? "100px" : "0px", // No gap when no search
          }}
        >
          {/* Search section */}
          {showSearch && (
            <div style={{ position: "relative", flex: 1 }}>
              <Search
                size={16}
                color="#6b7280"
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
              <input
                type="text"
                placeholder="Search table..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: "8px 12px 8px 36px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  outline: "none",
                  fontSize: "14px",
                  minWidth: "150px",
                  maxWidth: "300px",
                  width: "100%",
                }}
              />
            </div>
          )}

          {/* Download buttons container */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center", // Ensure vertical alignment
            }}
          >
            {/* Excel button with conditional styling */}
            {showDownload && (
              <button
                onClick={downloadExcel}
                style={getButtonStyle("#10B981")}
                title="Export to Excel"
              >
                <Download size={14} />
                <Sheet size={14} />
              </button>
            )}

            {/* PDF button with conditional styling */}
            {showPDFDownload && (
              <button
                onClick={downloadPDF}
                style={getButtonStyle("#EF4444")}
                title="Export to PDF"
              >
                <Download size={14} />
                <FileText size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table Section - This takes remaining space */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          position: "relative",
          minHeight: 0,
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0",
            position: "relative",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              zIndex: 20,
              ...headerStyle,
            }}
          >
            <tr style={{ borderBottom: "2px solid #d1d5db" }}>
              {tableType === "expandable" && (
                <th
                  style={{
                    width: "40px",
                    padding: "12px 8px",
                    background: item.headerColor,
                    position: "sticky",
                    top: 0,
                    zIndex: 21,
                    borderRight: "1px solid #d1d5db",
                    borderTopLeftRadius: "8px",
                  }}
                ></th>
              )}
              {visibleColumns.map((column, index) => (
                <th
                  key={column.key}
                  style={{
                    padding: "12px 16px",
                    textAlign: "center",
                    fontWeight: "600",
                    color: item.headerTextColor || "#374151",
                    background: item.headerColor,
                    fontSize: "14px",
                    cursor: enableSorting ? "pointer" : "default",
                    position: "sticky",
                    top: 0,
                    zIndex: 21,
                    borderRight:
                      index < visibleColumns.length - 1
                        ? "1px solid #d1d5db"
                        : "none",
                    borderTopLeftRadius:
                      tableType !== "expandable" && index === 0 ? "8px" : "0",
                    borderTopRightRadius:
                      index === visibleColumns.length - 1 ? "8px" : "0",
                  }}
                  onClick={() => enableSorting && handleSort(column.key)}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      justifyContent: "center",
                    }}
                  >
                    {column.label}
                    {enableSorting &&
                      sortConfig.key === column.key &&
                      (sortConfig.direction === "ascending" ? (
                        <ChevronUp size={14} />
                      ) : (
                        <ChevronDown size={14} />
                      ))}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row, rowIndex) => (
              <React.Fragment key={rowIndex}>
                <tr
                  style={{
                    borderBottom: "1px solid #d1d5db",
                    backgroundColor: expandedRows[rowIndex]
                      ? "#f8fafc"
                      : "transparent",
                  }}
                >
                  {tableType === "expandable" && (
                    <td
                      style={{
                        padding: "12px 8px",
                        borderRight: "1px solid #d1d5db",
                        borderBottom: "1px solid #d1d5db",
                      }}
                    >
                      <button
                        onClick={() => toggleRow(rowIndex)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          padding: "4px",
                          borderRadius: "4px",
                        }}
                      >
                        {expandedRows[rowIndex] ? (
                          <Minus size={16} color="#64748b" />
                        ) : (
                          <Plus size={16} color="#64748b" />
                        )}
                      </button>
                    </td>
                  )}
                  {visibleColumns.map((column, index) => (
                    <td
                      key={column.key}
                      style={{
                        padding: "0px 5px",
                        fontSize: "14px",
                        color: "#374151",
                        textAlign: column.alignment || "left",
                        borderRight:
                          index < visibleColumns.length - 1
                            ? "1px solid #d1d5db"
                            : "none",
                        borderBottom: "1px solid #d1d5db",
                      }}
                    >
                      {column.type === "badge" ? (
                        <span
                          style={{
                            backgroundColor: getBadgeColor(row[column.key]),
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                          }}
                        >
                          {row[column.key]}
                        </span>
                      ) : (
                        (() => {
                          const value = row[column.key];

                          if (typeof value === "boolean") {
                            return value ? "Yes" : "No";
                          }

                          const isDateField =
                            column.type === "date" ||
                            (typeof value === "string" &&
                              /\d{4}-\d{2}-\d{2}/.test(value));

                          if (isDateField && value) {
                            return formatDateForDisplay(value);
                          }

                          return value !== null &&
                            value !== undefined &&
                            value !== ""
                            ? value
                            : "NA";
                        })()
                      )}
                    </td>
                  ))}
                </tr>
                {tableType === "expandable" && expandedRows[rowIndex] && (
                  <tr>
                    <td
                      colSpan={visibleColumns.length + 1}
                      style={{
                        padding: 0,
                        position: "relative",
                        borderBottom: "1px solid #d1d5db",
                      }}
                    >
                      <div
                        style={{
                          padding: "16px 16px 16px 60px",
                          backgroundColor: "#f8fafc",
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: "white",
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                            position: "relative",
                            zIndex: 5,
                          }}
                        >
                          <div
                            style={{
                              padding: "16px",
                              borderBottom: "1px solid #e5e7eb",
                            }}
                          >
                            <h4
                              style={{
                                margin: 0,
                                fontSize: "16px",
                                fontWeight: "600",
                                textAlign: "center",
                              }}
                            >
                              Detailed view
                            </h4>
                          </div>
                          <div
                            style={{
                              overflowX: "auto",
                              maxHeight: "300px",
                              overflowY: "auto",
                              position: "relative",
                            }}
                          >
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                position: "relative",
                              }}
                            >
                              <thead
                                style={{
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 25,
                                }}
                              >
                                <tr
                                  style={{
                                    ...subHeaderStyle,
                                    borderBottom: "1px solid #e5e7eb",
                                  }}
                                >
                                  {visibleSubColumns.map((col, index) => (
                                    <th
                                      key={col.key}
                                      style={{
                                        padding: "12px 16px",
                                        textAlign: "center",
                                        fontWeight: "600",
                                        background: item.subHeaderColor,
                                        color: item.subHeaderTextColor,
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 26,
                                        borderRight:
                                          index < visibleSubColumns.length - 1
                                            ? "1px solid #d1d5db"
                                            : "none",
                                      }}
                                    >
                                      {col.label}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  {visibleSubColumns.map((col, index) => (
                                    <td
                                      key={col.key}
                                      style={{
                                        padding: "0px 5px",
                                        borderBottom: "1px solid #d1d5db",
                                        textAlign: col.alignment || "left",
                                        borderRight:
                                          index < visibleSubColumns.length - 1
                                            ? "1px solid #d1d5db"
                                            : "none",
                                      }}
                                    >
                                      {(() => {
                                        const value = row[col.key];

                                        if (typeof value === "boolean") {
                                          return value ? "Yes" : "No";
                                        }
                                        const isDateField =
                                          col.type === "date" ||
                                          (typeof value === "string" &&
                                            /\d{4}-\d{2}-\d{2}/.test(value));

                                        if (isDateField && value) {
                                          return formatDateForDisplay(value);
                                        }

                                        return value !== null &&
                                          value !== undefined &&
                                          value !== ""
                                          ? value
                                          : "NA";
                                      })()}
                                    </td>
                                  ))}
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* No records message */}
      {currentRows.length === 0 && (
        <div
          style={{
            flexShrink: 0,
            padding: "24px",
            textAlign: "center",
            color: "#6b7280",
          }}
        >
          No matching records found
        </div>
      )}

      {/* Pagination Section */}
      {showPagination && sortedData.length > 0 && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "16px",
            padding: "8px 0",
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "white",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "14px", color: "#6b7280" }}>
              Rows per page:
            </span>
            <select
              value={currentRowsPerPage}
              onChange={(e) => {
                const newValue = Number(e.target.value);
                if (onTableRowsPerPageChange) {
                  onTableRowsPerPageChange(newValue);
                }
              }}
              style={{
                padding: "4px 8px",
                borderRadius: "4px",
                border: "1px solid #d1d5db",
                backgroundColor: "white",
                fontSize: "14px",
                color: "#374151",
              }}
            >
              {generatePaginationOptions(sortedData.length).map((size) => (
                <option key={size} value={size}>
                  {size === sortedData.length ? `All (${size})` : size}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "14px", color: "#6b7280" }}>
              {indexOfFirstRow + 1}-{Math.min(indexOfLastRow, totalRows)} of{" "}
              {totalRows}
            </span>
            <button
              onClick={() => {
                if (onTablePageChange && currentPage > 1) {
                  onTablePageChange(currentPage - 1);
                }
              }}
              disabled={currentPage === 1}
              style={{
                padding: "4px 8px",
                backgroundColor: currentPage === 1 ? "#f3f4f6" : "#e5e7eb",
                color: currentPage === 1 ? "#9ca3af" : "#374151",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
              }}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => {
                if (onTablePageChange && currentPage < totalPages) {
                  onTablePageChange(currentPage + 1);
                }
              }}
              disabled={currentPage >= totalPages}
              style={{
                padding: "4px 8px",
                backgroundColor:
                  currentPage >= totalPages ? "#f3f4f6" : "#e5e7eb",
                color: currentPage >= totalPages ? "#9ca3af" : "#374151",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
              }}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableComponent;
